import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCapIcon, BookOpenIcon, HeartIcon, UsersIcon, ShieldCheckIcon, ClockIcon, DollarSignIcon, AwardIcon, CheckCircleIcon, ArrowRightIcon, StarIcon, QuoteIcon, CheckIcon } from 'lucide-react';
import { getFindDegreesTemplate } from '../data/findDegreesTemplates';
import { usePageMeta } from '../hooks/usePageMeta';
const benefitIconMap: Record<string, React.ElementType> = {
  GraduationCapIcon,
  BookOpenIcon,
  HeartIcon,
  UsersIcon,
  ShieldCheckIcon,
  ClockIcon,
  DollarSignIcon,
  AwardIcon
};
export function FindDegreesPage() {
  const {
    template
  } = useParams<{
    template: string;
  }>();
  const navigate = useNavigate();
  const data = template ? getFindDegreesTemplate(template) : undefined;
  usePageMeta(data?.metaTitle || 'Find Your Degree | OnlineCollegeGuide', data?.metaDescription || 'Find accredited online degree programs that fit your goals.', {
    ogType: 'website'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [template]);
  if (!data) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4" data-id="element-1063" data-mp="3ox3upybmsnlv">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1064" data-mp="3jxzongk075zr">Page Not Found</h1>
        <p className="text-gray-600 mb-8" data-id="element-1065" data-mp="3jxr73bcxrci8">
          We couldn't find the page you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1066" data-mp="3f0r4jafaorvv">
          Return Home
        </Link>
      </div>;
  }
  const accent = data.accentColor;
  const accentDark = data.accentColorDark;
  const handleCta = () => {
    navigate(data.ctaHref);
  };
  return <main className="min-h-screen bg-white" data-id="element-1067" data-mp="2w8ol5rb9qxhx">
      {/* Minimal Top Bar */}
      <div className="bg-white border-b border-gray-100" data-id="element-1068" data-mp="2r9lpuyl3c5n5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-1069" data-mp="2ra3tiga31m53">
          <Link to="/" className="flex items-center" data-id="element-1070" data-mp="2hketu7m37fhd">
            <img src="https://cdn.magicpatterns.com/uploads/t42RRFzoQcZTQwKva1oBHJ/OnlineCollegeGuide_logo_design_1.webp" alt="OnlineCollegeGuide" className="h-10 w-auto object-contain" data-id="element-1071" data-mp="2ml2qp9wzyqji" />
          </Link>
          <div className="hidden sm:flex items-center text-xs font-semibold text-gray-500 tracking-wider uppercase" data-id="element-1072" data-mp="2cog86rs58bkn">
            <ShieldCheckIcon className="w-4 h-4 mr-1.5 text-green-600" data-id="element-1073" data-mp="35b0zxtcj6poq" />
            Trusted by 2M+ Students
          </div>
        </div>
      </div>

      {/* Hero - light theme matching reference layout */}
      <section className="bg-gray-50 relative overflow-hidden" data-id="element-1074" data-mp="2vkz9ww0max76">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20" data-id="element-1075" data-mp="2vjee732kh87q">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-1076" data-mp="3j9ifv4w72xw3">
            {/* Left: Copy */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-1077" data-mp="3o3wi9r99y5a9">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest text-white mb-6" style={{
              backgroundColor: '#1B365D'
            }} data-id="element-1078" data-mp="1mep8f0ma1tde">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: '#D4A943'
              }} data-id="element-1079" data-mp="oqf4gg2xkvjn" />
                {data.heroEyebrowBadge || data.eyebrow}
              </span>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-gray-900 mb-6 tracking-tight" data-id="element-1080" data-mp="2f0ifvniipw6i">
                {data.heroHeadlinePrefix && <>{data.heroHeadlinePrefix} </>}
                {data.heroHeadlineHighlight && <span style={{
                color: '#1B365D'
              }} data-id="element-1081" data-mp="jnfzjiyd5xgh">
                    {data.heroHeadlineHighlight}
                  </span>}
                {data.heroHeadlineSuffix && <>
                    <br data-id="element-1082" data-mp="xirvtus63vzi" />
                    {data.heroHeadlineSuffix}
                  </>}
                {!data.heroHeadlinePrefix && !data.heroHeadlineHighlight && !data.heroHeadlineSuffix && data.headline}
              </h1>

              <p className="text-base sm:text-lg text-gray-600 max-w-xl leading-relaxed" data-id="element-1083" data-mp="1cc4rardj4qfk">
                {data.subheadline}
              </p>
            </motion.div>

            {/* Right: Step indicator + Card */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-1084" data-mp="1q9jo4h6440hk">
              {/* Step Indicator */}
              <div className="flex items-center justify-between mb-5 px-2 sm:px-4" data-id="element-1085" data-mp="1qhdhkhbmaih8">
                <div className="flex items-center gap-2.5" data-id="element-1086" data-mp="1mfig9ctos53g">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#D4A943'
                }} data-id="element-1087" data-mp="2j47y535bcxpf">
                    <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-1088" data-mp="29clrzl7u1otj" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900" data-id="element-1089" data-mp="25cb9xa6rn7yb">
                    {data.heroStep1Label || 'Profile Complete'}
                  </span>
                </div>
                <div className="flex-1 h-px bg-gray-300 mx-3 sm:mx-4" data-id="element-1090" data-mp="2x2xx6obp17gf" />
                <div className="flex items-center gap-2.5" data-id="element-1091" data-mp="v5nqwfr76vtx">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#1B365D'
                }} data-id="element-1092" data-mp="v8a4ox4om7kl">
                    <span className="text-xs font-bold text-white" data-id="element-1093" data-mp="qeywoso4vkr3">2</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-500" data-id="element-1094" data-mp="cf6b1brgrupz">
                    {data.heroStep2Label || 'Confirm Email'}
                  </span>
                </div>
              </div>

              {/* Card */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 sm:p-10 text-center" data-id="element-1095" data-mp="2il24fcbpzgv">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2" data-id="element-1096" data-mp="7h4ryqu1fvei">
                  {data.heroCardHeading || data.headline}
                </h2>
                {data.heroCardSubtext && <p className="text-sm font-semibold mb-6" style={{
                color: '#1B365D'
              }} data-id="element-1097" data-mp="21tul36cl10ku">
                    {data.heroCardSubtext}
                  </p>}
                <button type="button" onClick={handleCta} className="group inline-flex items-center justify-center text-white font-bold py-3.5 px-8 rounded-lg text-base transition-all duration-200 shadow-md hover:shadow-lg" style={{
                backgroundColor: '#D4A943'
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#B38B2E';
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#D4A943';
              }} data-id="element-1098" data-mp="3w3j7nht5gaky">
                  {data.ctaLabel}
                  <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" data-id="element-1099" data-mp="1tuskzkj785pu" />
                </button>
                {data.heroCardHelper && <p className="mt-6 text-xs sm:text-sm text-gray-500 leading-relaxed max-w-xs mx-auto" data-id="element-1100" data-mp="23m6nm16g5ln7">
                    {data.heroCardHelper}
                  </p>}
              </div>

              {/* Trust strip below card */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-gray-500" data-id="element-1101" data-mp="1fvb3zrc59wr1">
                {data.trustItems.map(item => <div key={item} className="flex items-center" data-id="element-1102" data-mp="1kt2sschht2r0">
                    <CheckCircleIcon className="w-3.5 h-3.5 text-green-600 mr-1.5 flex-shrink-0" data-id="element-1103" data-mp="1puico17xp37t" />
                    {item}
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-1104" data-mp="plsgbpukuftx">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1105" data-mp="pkh9ti9qytz4">
          <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-1106" data-mp="kn8rnholmpxv">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{
            color: accent
          }} data-id="element-1107" data-mp="fvh83r3v1kh8">
              {data.benefitsHeading}
            </h2>
            <div className="w-16 h-1 mx-auto rounded-full" style={{
            backgroundColor: '#D4A943'
          }} data-id="element-1108" data-mp="5xt5kf00j8oi" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1109" data-mp="3t1r4dwphitnt">
            {data.benefits.map((benefit, i) => {
            const Icon = benefitIconMap[benefit.icon] || BookOpenIcon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow" data-id="element-1110" data-mp="1r9l4t51u1mpz" data-mpi={i}>
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{
                backgroundColor: `${accent}15`
              }} data-id="element-1111" data-mp="2eeafjgmffllh">
                    <Icon className="w-6 h-6" style={{
                  color: accent
                }} data-id="element-1112" data-mp="2ifdu7zsqnbl4" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{
                color: accent
              }} data-id="element-1113" data-mp="1qog3h19xm12a">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1114" data-mp="1lbrhoseth7td">
                    {benefit.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Featured Schools */}
      <section className="py-14 bg-gray-50 border-y border-gray-200" data-id="element-1115" data-mp="1za7pkmof14d3">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1116" data-mp="1z8wm6nyvtymi">
          <h2 className="text-center text-sm font-bold uppercase tracking-widest mb-8 text-gray-500" data-id="element-1117" data-mp="249bx78voyu1b">
            {data.schoolsHeading}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1118" data-mp="1ube9cmhquz98">
            {data.schoolNames.map(name => <div key={name} className="bg-white border border-gray-200 rounded-lg p-4 text-center flex items-center justify-center min-h-[80px]" data-id="element-1119" data-mp="dwcxl4u26j37">
                <span className="text-sm font-semibold leading-tight" style={{
              color: accent
            }} data-id="element-1120" data-mp="2li4qmb3ncsmt">
                  {name}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {data.testimonial && <section className="py-16 lg:py-20 bg-white" data-id="element-1121" data-mp="21uxc8dai7n8q">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1122" data-mp="21wi41v5fos2w">
            <QuoteIcon className="w-12 h-12 mx-auto mb-6 opacity-30" style={{
          color: accent
        }} data-id="element-1123" data-mp="1xssizfnywsmm" />
            <div className="flex items-center justify-center gap-1 mb-4" data-id="element-1124" data-mp="1nvxkgwrp2s8c">
              {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="w-5 h-5 fill-current" style={{
            color: '#D4A943'
          }} data-id="element-1125" data-mp="3n7gw5j4duelp" />)}
            </div>
            <p className="text-xl sm:text-2xl font-medium leading-relaxed mb-6 italic" style={{
          color: accent
        }} data-id="element-1126" data-mp="3pqvcb25n5bmj">
              "{data.testimonial.quote}"
            </p>
            <p className="font-bold text-gray-900" data-id="element-1127" data-mp="8czr9xsdapdg">{data.testimonial.name}</p>
            <p className="text-sm text-gray-500" data-id="element-1128" data-mp="36u3oqgj2gaco">{data.testimonial.detail}</p>
          </div>
        </section>}

      {/* Bottom CTA */}
      <section className="py-16 lg:py-20" style={{
      backgroundColor: accentDark
    }} data-id="element-1129" data-mp="3btybq8zq0fsg">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1130" data-mp="3gqylouzoy2ou">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white mb-4 leading-tight" data-id="element-1131" data-mp="3ls4gcycww010">
            {data.bottomCtaHeading}
          </h2>
          <p className="text-base sm:text-lg text-gray-300 mb-8 leading-relaxed" data-id="element-1132" data-mp="1113wki7110fm">
            {data.bottomCtaBody}
          </p>
          <button type="button" onClick={handleCta} className="group inline-flex items-center justify-center text-white font-bold py-4 px-10 rounded-lg text-base tracking-wide transition-all duration-200 shadow-lg hover:shadow-xl" style={{
          backgroundColor: '#D4A943'
        }} onMouseEnter={e => {
          ;
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#B38B2E';
        }} onMouseLeave={e => {
          ;
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#D4A943';
        }} data-id="element-1133" data-mp="ylp16eck6gix">
            {data.bottomCtaLabel}
            <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" data-id="element-1134" data-mp="1mbrqwnt03aed" />
          </button>
          <p className="mt-5 text-xs text-gray-400 uppercase tracking-widest" data-id="element-1135" data-mp="21a3ocwo76n6l">
            Complementary • No Obligation • Takes 2 Minutes
          </p>
        </div>
      </section>

      {/* Footer mini */}
      <footer className="bg-gray-900 text-gray-400 text-xs py-6" data-id="element-1136" data-mp="2fwiquqyzlsxs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-2" data-id="element-1137" data-mp="2jxvyn6ln467c">
          <p data-id="element-1138" data-mp="25ycs2i8h92x5">
            &copy; {new Date().getFullYear()} OnlineCollegeGuide. All rights
            reserved.
          </p>
          <p className="max-w-3xl mx-auto leading-relaxed" data-id="element-1139" data-mp="2avuzu5ardc3k">
            Advertiser Disclosure: OnlineCollegeGuide.com is an
            advertising-supported site. Featured schools compensate us, which
            does not influence our editorial content.
          </p>
          <div className="flex justify-center gap-4 pt-2" data-id="element-1140" data-mp="1n7ve5jsb0bvu">
            <Link to="/privacy-policy" className="hover:text-white transition-colors" data-id="element-1141" data-mp="1n58vanzyq9yb">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="hover:text-white transition-colors" data-id="element-1142" data-mp="1dah0tt1og37r">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </main>;
}